import { alpha, createTheme } from '@mui/material/styles';

const fontFamily = [
  'Manrope',
  'system-ui',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'sans-serif',
].join(', ');

const createMuiTheme = (mode) => {
  const isDark = mode === 'dark';
  const primaryMain = '#2563eb';
  const palette = {
    mode,
    primary: { main: primaryMain },
    secondary: { main: '#f59e0b' },
    action: {
      hover: isDark ? 'rgba(148, 163, 184, 0.12)' : 'rgba(15, 23, 42, 0.06)',
      selected: isDark ? 'rgba(59, 130, 246, 0.18)' : 'rgba(37, 99, 235, 0.12)',
    },
    background: {
      default: isDark ? '#0b1220' : '#f7f9fc',
      paper: isDark ? '#0f172a' : '#ffffff',
    },
    text: {
      primary: isDark ? '#e2e8f0' : '#0f172a',
      secondary: isDark ? '#94a3b8' : '#64748b',
    },
    divider: isDark ? '#1f2937' : '#e5e7eb',
  };
  const headerBg = alpha(primaryMain, isDark ? 0.16 : 0.06);
  const rowStripe = alpha(primaryMain, isDark ? 0.08 : 0.04);
  const footerBg = alpha(primaryMain, isDark ? 0.08 : 0.04);
  return createTheme({
    palette,
    typography: {
      fontFamily,
      button: {
        textTransform: 'none',
        fontWeight: 700,
      },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: palette.background.default,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: palette.text.secondary,
            '&.Mui-focused': {
              color: palette.primary.main,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: palette.text.secondary,
            '&.Mui-focused': {
              color: palette.primary.main,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.primary.main,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 16,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${palette.divider}`,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          rowHeight: 44,
          columnHeaderHeight: 44,
        },
        styleOverrides: {
          root: {
            borderColor: palette.divider,
            color: palette.text.primary,
            backgroundColor: palette.background.paper,
            // Mobile: ensure horizontal scroll
            '@media (max-width: 640px)': {
              minWidth: 'min-content',
            },
          },
          columnHeaders: {
            backgroundColor: headerBg,
            borderBottom: `1px solid ${palette.divider}`,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontSize: '0.75rem',
            fontWeight: 700,
          },
          columnSeparator: {
            color: palette.divider,
          },
          cell: {
            borderBottom: `1px solid ${palette.divider}`,
          },
          row: {
            '&:nth-of-type(even)': {
              backgroundColor: rowStripe,
            },
            '&:hover': {
              backgroundColor: palette.action.hover,
            },
          },
          footerContainer: {
            borderTop: `1px solid ${palette.divider}`,
            backgroundColor: footerBg,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: palette.divider,
          },
          head: {
            fontWeight: 700,
            color: palette.text.primary,
          },
        },
      },
    },
  });
};

export default createMuiTheme;
